import api from './api';
import type { TablemaintRow } from './tablemaint.service';

/**
 * Fuel-Surcharge-Tariff support client. Base-record CRUD goes through the generic
 * tablemaint engine (/tablemaint/fsc-tariff); this covers the master-detail extras:
 * the preloaded bill-to options, the tariff-lines sub-editor, the read-only
 * rate-schedule preview, and the two clone ops (New Revision / Dupe).
 */

export interface BillToOption {
  recno: number;
  name: string;
  mas90code: string;
  address: string;
  city: string;
  state: string;
}

export interface FscTariffFormOptions {
  billTos: BillToOption[];
}

export interface TariffLine {
  recno: number; // 0 for an unsaved new line
  fuelindex: string;
  effectivedate: string;
  fuelrate: string;
}

export interface TariffLinesResponse {
  tariffType: string;
  rateType: string;
  expired: boolean;
  lines: TariffLine[];
}

export interface TariffPreviewRow {
  key: string;
  keyEnd: string;
  rate: string;
}

export interface TariffPreviewResponse {
  tariffType: string;
  rateType: string;
  indexSource: string | null;
  currentIndex: string | null;
  rows: TariffPreviewRow[];
}

/** A bill-to attached to a tariff via the multi-bill-to junction, in ADDITION to
 *  the tariff's primary fsc_tariff.billto (the one V1 uses). */
export interface AdditionalBillTo {
  billto: number;
  name: string;
  code: string;
}

const base = '/fsc-tariffs';

const fscTariffsService = {
  async formOptions(signal?: AbortSignal): Promise<FscTariffFormOptions> {
    const res = await api.get(`${base}/form-options`, { signal });
    return res.data as FscTariffFormOptions;
  },

  async getLines(recno: number, signal?: AbortSignal): Promise<TariffLinesResponse> {
    const res = await api.get(`${base}/${recno}/lines`, { signal });
    return res.data as TariffLinesResponse;
  },

  async getLinesPreview(recno: number, signal?: AbortSignal): Promise<TariffPreviewResponse> {
    const res = await api.get(`${base}/${recno}/lines-preview`, { signal });
    return res.data as TariffPreviewResponse;
  },

  async saveLines(
    recno: number,
    body: { deletes: number[]; lines: TariffLine[] },
  ): Promise<{ recno: number }> {
    const res = await api.put(`${base}/${recno}/lines`, body);
    return res.data as { recno: number };
  },

  async newRevision(payload: TablemaintRow & { sourceRecno: number }): Promise<{ recno: number }> {
    const res = await api.post(`${base}/new-revision`, payload);
    return res.data as { recno: number };
  },

  /** "Dupe": copy a tariff into a BRAND-NEW tariff number (revision 1), carrying
   *  the edited base fields + the source's rate lines. The source is untouched —
   *  unlike New Revision, which supersedes it. */
  async dupe(payload: TablemaintRow & { sourceRecno: number }): Promise<{ recno: number; tariffnumber: number }> {
    const res = await api.post(`${base}/dupe`, payload);
    return res.data as { recno: number; tariffnumber: number };
  },

  // Multi-bill-to junction: bill-tos a tariff applies to BEYOND its primary
  // fsc_tariff.billto. Keyed by tariffnumber (shared across revisions).
  async getBillTos(tariffnumber: number, signal?: AbortSignal): Promise<AdditionalBillTo[]> {
    const res = await api.get(`${base}/${tariffnumber}/billtos`, { signal });
    return res.data as AdditionalBillTo[];
  },

  async saveBillTos(
    tariffnumber: number,
    billtos: number[],
  ): Promise<{ tariffnumber: number; billtos: number[] }> {
    const res = await api.put(`${base}/${tariffnumber}/billtos`, { billtos });
    return res.data as { tariffnumber: number; billtos: number[] };
  },
};

export default fscTariffsService;
